import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Slide
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const API_URL = process.env.REACT_APP_API_URL;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ratingOptions = ['', '1', '2', '3', '4', '5'];

const labels = {
  th: {
    title: 'แก้ไขรีวิว', save: 'บันทึก', placeName: 'ชื่อสถานที่',
    content: 'เนื้อหารีวิว', location: 'ที่อยู่', website: 'เว็บไซต์',
    productRating: 'คะแนนสินค้า', serviceRating: 'คะแนนบริการ',
    locationRating: 'คะแนนทำเล', noRating: 'ไม่ระบุ'
  },
  en: {
    title: 'Edit Review', save: 'Save', placeName: 'Place Name',
    content: 'Review Content', location: 'Location', website: 'Website',
    productRating: 'Product Rating', serviceRating: 'Service Rating',
    locationRating: 'Location Rating', noRating: 'Not rated'
  },
  zh: {
    title: '编辑评论', save: '保存', placeName: '地点名称',
    content: '评论内容', location: '地址', website: '网站',
    productRating: '产品评分', serviceRating: '服务评分',
    locationRating: '位置评分', noRating: '未评分'
  }
};

const EditReviewModal = ({ open, review, onClose, onSaved, language = 'th' }) => {
  const l = labels[language] || labels.en;

  // Strip HTML tags for editing
  const plainContent = (review?.review_content || '').replace(/<[^>]*>/g, '');

  const [form, setForm] = useState({
    place_name: review?.place_name || '',
    review_content: plainContent,
    location: review?.location || '',
    website_link: review?.website_link || '',
    product_rating: review?.product_rating || '',
    service_rating: review?.service_rating || '',
    location_rating: review?.location_rating || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.put(
        `${API_URL}/reviews/${review.id}`,
        {
          place_name: form.place_name,
          review_content: form.review_content,
          location: form.location,
          website_link: form.website_link,
          product_rating: form.product_rating || null,
          service_rating: form.service_rating || null,
          location_rating: form.location_rating || null
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      onSaved && onSaved(res.data.review);
    } catch (err) {
      console.error('❌ [EditReviewModal] Save error:', err);
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {l.title}
          </Typography>
          <Button color="inherit" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : l.save}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto', width: '100%' }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}

        <TextField
          label={l.placeName}
          value={form.place_name}
          onChange={handleChange('place_name')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.content}
          value={form.review_content}
          onChange={handleChange('review_content')}
          fullWidth
          multiline
          minRows={6}
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.location}
          value={form.location}
          onChange={handleChange('location')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.website}
          value={form.website_link}
          onChange={handleChange('website_link')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label={l.productRating}
            value={form.product_rating}
            onChange={handleChange('product_rating')}
            sx={{ minWidth: 160 }}
          >
            {ratingOptions.map(v => (
              <MenuItem key={v} value={v}>{v === '' ? l.noRating : v}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={l.serviceRating}
            value={form.service_rating}
            onChange={handleChange('service_rating')}
            sx={{ minWidth: 160 }}
          >
            {ratingOptions.map(v => (
              <MenuItem key={v} value={v}>{v === '' ? l.noRating : v}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={l.locationRating}
            value={form.location_rating}
            onChange={handleChange('location_rating')}
            sx={{ minWidth: 160 }}
          >
            {ratingOptions.map(v => (
              <MenuItem key={v} value={v}>{v === '' ? l.noRating : v}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditReviewModal;
